/**
 * ClipCommitAssociations Component
 *
 * Displays all commits that reference a specific clip, showing:
 * - Which timeline versions use this clip
 * - When the clip was added to each commit
 * - Timeline position and clip state information
 * - Links to navigate to each commit
 */
import { useClipCommits } from '@/hooks/useClips'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  GitCommit,
  Calendar,
  Clock,
  Film,
  ExternalLink
} from 'lucide-react'
import type { Clip } from '@/types/database'

interface ClipCommitAssociationsProps {
  clip: Clip
}

export function ClipCommitAssociations({ clip }: ClipCommitAssociationsProps) {
  const { data, isLoading, error } = useClipCommits(clip.id)

  /**
   * Format frames to timecode
   */
  const formatFramesToTimecode = (frames?: number, fps: number = 24): string => {
    if (!frames && frames !== 0) return '-'

    const totalSeconds = frames / fps
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = Math.floor(totalSeconds % 60)
    const remainingFrames = Math.floor(frames % fps)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${remainingFrames.toString().padStart(2, '0')}`
  }

  /**
   * Format date/time
   */
  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString)
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString()
  }

  /**
   * Navigate to commit
   */
  const handleViewCommit = (commitId: string) => {
    // Navigate to the project commits page with commit query parameter
    const url = `/projects/${clip.project_id}/commits?commit=${commitId}`
    window.location.href = url
  }

  // Hide the entire component if there's an error (commits are optional)
  if (error) {
    console.warn('Failed to load clip commits:', error)
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <GitCommit className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Commit Associations</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Loading commits...
          </p>
        )}

        {!isLoading && !error && data?.associations.length === 0 && (
          <div className="text-center py-8">
            <GitCommit className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              No commits reference this clip yet
            </p>
            <p className="text-xs text-muted-foreground">
              This clip will appear here once it's added to a timeline commit
            </p>
          </div>
        )}

        {!isLoading && !error && data && data.associations.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              This clip appears in <strong>{data.total}</strong> commit{data.total !== 1 ? 's' : ''}
            </p>

            {data.associations.map((association) => (
              <Card key={association.id} className="p-4">
                <div className="space-y-3">
                  {/* Commit header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm">
                          {association.commit.sequence_name}
                        </h4>
                        <Badge variant="outline">
                          Commit #{association.commit.commit_number}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {association.commit.message}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewCommit(association.commit_id)}
                      title="View commit"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Commit metadata */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {association.commit.author_name && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <span className="font-medium">Author:</span>
                        <span>{association.commit.author_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDateTime(association.created_at)}</span>
                    </div>
                  </div>

                  {/* Clip state in this commit */}
                  {(association.timeline_position_frames !== null ||
                    association.track_name ||
                    association.duration_frames !== null) && (
                    <div className="pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        Clip State in Timeline:
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {association.track_name && (
                          <div>
                            <span className="text-muted-foreground">Track:</span>{' '}
                            <Badge variant="secondary" className="text-xs">
                              {association.track_name}
                            </Badge>
                          </div>
                        )}
                        {association.timeline_position_frames !== null && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Position:</span>{' '}
                            <span className="font-mono">
                              {formatFramesToTimecode(association.timeline_position_frames, clip.fps || 24)}
                            </span>
                          </div>
                        )}
                        {association.duration_frames !== null && (
                          <div className="flex items-center gap-1">
                            <Film className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Duration:</span>{' '}
                            <span className="font-mono">
                              {formatFramesToTimecode(association.duration_frames, clip.fps || 24)}
                            </span>
                          </div>
                        )}
                        {association.source_in_frames !== null && (
                          <div className="col-span-2 text-muted-foreground">
                            <span>Source:</span>{' '}
                            <span className="font-mono">
                              {formatFramesToTimecode(association.source_in_frames, clip.fps || 24)}
                            </span>
                            {' → '}
                            <span className="font-mono">
                              {formatFramesToTimecode(association.source_out_frames, clip.fps || 24)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
